import {Injectable, isDevMode} from '@angular/core';
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {FullChild} from "../../interfaces/FullChild";
import firebase from "firebase/compat/app";
import {environment} from "../../../environments/environment";
import {NotificationService} from "../../services/notification.service";
import {TasksService} from "../task/tasks.service";
import {Task} from "../task/models/task.model";
import {combineLatest, firstValueFrom, Observable} from "rxjs";
import {map, shareReplay, take} from "rxjs/operators";
import {collection, getCountFromServer, getFirestore, query, where} from "firebase/firestore";

@Injectable({
  providedIn: 'root'
})
export class EducationService {
  env = isDevMode() ? environment.testMode : environment.productionMode

  private readonly FIRESTORE_IN_LIMIT = 10;

  constructor(private readonly mFireStore: AngularFirestore,
              private notificationService: NotificationService,
              private readonly taskService: TasksService
  ) {
  }

  /** Chunk an array into sub-arrays of maxSize (for Firestore 'in' limit) */
  private chunkArray<T>(arr: T[], maxSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += maxSize) {
      chunks.push(arr.slice(i, i + maxSize));
    }
    return chunks;
  }

  individualSearchResults(centreCode: string, startDate ?: Date | undefined, endDate ?: Date | undefined, attendance ?: string | null, submittedBy ?: string | null, sessionType ?: string | null, vounteer ?: string
  ) {
    console.log('centercode', centreCode)
    let myQuery: firebase.firestore.Query<any> = this.mFireStore.firestore.collection(this.env.education).orderBy('sessionDate', 'desc')
    if (centreCode != 'All' && centreCode) myQuery = myQuery.where('centreCode', '==', centreCode)
    if (startDate && endDate) myQuery = myQuery.where('sessionDate', '>=', startDate).where('sessionDate', '<=', endDate)
    if (attendance && attendance != 'All') myQuery = myQuery.where('present', '==', attendance)
    if (submittedBy && submittedBy != 'All') myQuery = myQuery.where('submittedBy', '==', submittedBy)
    if (sessionType && sessionType != 'All') myQuery = myQuery.where('sessionType', '==', sessionType)
    if (vounteer && vounteer != 'All') myQuery = myQuery.where('volunteerName', '==', vounteer)
    return myQuery.get()
  }

  individualHighlights(
    centreCode: string,
    startDate?: Date | undefined,
    endDate?: Date | undefined,
    attendance?: string | null,
    submittedBy?: string | null,
    sessionType?: string | null,
    vounteer?: string
  ) {

    console.log('centercode', centreCode)

    let myQuery: firebase.firestore.Query<any> =
      this.mFireStore.firestore
        .collection(this.env.education)
        .orderBy('sessionDate', 'desc')

    // ✅ ADD THIS LINE (Highlight filter)
    myQuery = myQuery.where('isHighlight', '==', true)

    if (centreCode != 'All' && centreCode)
      myQuery = myQuery.where('centreCode', '==', centreCode)

    if (startDate && endDate)
      myQuery = myQuery
        .where('sessionDate', '>=', startDate)
        .where('sessionDate', '<=', endDate)

    if (attendance && attendance != 'All')
      myQuery = myQuery.where('present', '==', attendance)

    if (submittedBy && submittedBy != 'All')
      myQuery = myQuery.where('submittedBy', '==', submittedBy)

    if (sessionType && sessionType != 'All')
      myQuery = myQuery.where('sessionType', '==', sessionType)

    return myQuery.get()
  }


  individualDashboardSearchData(centreCode: string | string[], startDate?: Date | null | undefined, endDate?: Date | null | undefined,){
    const codes: string[] = Array.isArray(centreCode)
      ? centreCode.filter(c => !!c && c !== 'All')
      : (centreCode && centreCode !== 'All' ? [centreCode] : []);
    console.log('centercode(s)', codes)

    const buildQuery = (filterCodes: string[]): firebase.firestore.Query<any> => {
      let myQuery: firebase.firestore.Query<any> = this.mFireStore.firestore.collection(this.env.education).orderBy('sessionDate', 'desc');
      if (filterCodes.length === 1) myQuery = myQuery.where('centreCode', '==', filterCodes[0]);
      else if (filterCodes.length > 1) myQuery = myQuery.where('centreCode', 'in', filterCodes);
      if (startDate && endDate) myQuery = myQuery.where('sessionDate', '>=', startDate).where('sessionDate', '<=', endDate);
      myQuery = myQuery.where('present', '==', 'Present');
      return myQuery;
    };

    if (codes.length <= this.FIRESTORE_IN_LIMIT) {
      return buildQuery(codes).get();
    }

    // Chunk codes into batches of 10 and run parallel queries
    const chunks = this.chunkArray(codes, this.FIRESTORE_IN_LIMIT);
    return Promise.all(chunks.map(chunk => buildQuery(chunk).get())).then(snapshots => {
      const allDocs = snapshots.flatMap(s => s.docs);
      return { docs: allDocs } as any;
    });
  }
  // ========================================================
  // 📊 EDUCATION MONTHLY SUMMARY — pre-aggregated counters
  // ========================================================
  private readonly summaryCollection = 'educationMonthlySummary';

  private toJsDate(d: any): Date {
    if (!d) return new Date();
    if (d?.toDate) return d.toDate();
    if (d instanceof Date) return d;
    return new Date(d);
  }

  private getYearMonth(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  private sanitizeForDocId(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  private summaryDocId(teacherName: string, yearMonth: string, centreCode: string): string {
    return `${this.sanitizeForDocId(teacherName)}_${yearMonth}_${centreCode}`;
  }

  private getYearMonthsInRange(start: Date, end: Date): string[] {
    const months: string[] = [];
    let cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMo = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= endMo) {
      months.push(this.getYearMonth(cur));
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  }

  /**
   * Update pre-aggregated summary counter when sessions are added (delta=1) or deleted (delta=-1).
   * fields.individual, fields.group, fields.followUp: optional increments.
   */
  updateSummaryCounter(
    teacherName: string,
    centreCode: string,
    sessionDate: any,
    fields: { individual?: number; group?: number; followUp?: number }
  ): Promise<void> {
    if (!teacherName || !centreCode || !sessionDate) return Promise.resolve();
    const date = this.toJsDate(sessionDate);
    const yearMonth = this.getYearMonth(date);
    const docId = this.summaryDocId(teacherName, yearMonth, centreCode);

    const update: any = {
      teacherName,
      centreCode,
      yearMonth,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
    };
    if (fields.individual !== undefined && fields.individual !== 0) {
      update['individualCount'] = firebase.firestore.FieldValue.increment(fields.individual);
    }
    if (fields.group !== undefined && fields.group !== 0) {
      update['groupCount'] = firebase.firestore.FieldValue.increment(fields.group);
    }
    if (fields.followUp !== undefined && fields.followUp !== 0) {
      update['followUpCount'] = firebase.firestore.FieldValue.increment(fields.followUp);
    }

    return this.mFireStore.firestore
      .collection(this.summaryCollection)
      .doc(docId)
      .set(update, { merge: true });
  }

  /**
   * Query pre-aggregated summary for a date range + optional centre(s).
   * Supports single centreCode string, an array of centreCodes, or empty (all centres).
   * When dates are null, fetches ALL summary docs (no date filter).
   * Properly chunks yearMonth 'in' queries to stay within Firestore's limit of 10.
   */
  getSummaryForDateRange(
    centreCode: string | string[],
    startDate: Date | null | undefined,
    endDate: Date | null | undefined
  ): Promise<any[]> {
    const codes: string[] = Array.isArray(centreCode)
      ? centreCode.filter(c => !!c && c !== 'All')
      : (centreCode && centreCode !== 'All' ? [centreCode] : []);

    // ── No date filter → fetch ALL summary docs (optionally filtered by centre) ──
    if (!startDate || !endDate) {
      if (codes.length === 0) {
        return this.mFireStore.firestore.collection(this.summaryCollection).get()
          .then(snap => snap.docs.map(d => d.data()));
      }
      if (codes.length === 1) {
        return this.mFireStore.firestore.collection(this.summaryCollection)
          .where('centreCode', '==', codes[0]).get()
          .then(snap => snap.docs.map(d => d.data()));
      }
      // Multiple centres — chunk centre codes
      const centreChunks = this.chunkArray(codes, this.FIRESTORE_IN_LIMIT);
      return Promise.all(centreChunks.map(chunk =>
        this.mFireStore.firestore.collection(this.summaryCollection)
          .where('centreCode', 'in', chunk).get()
          .then(snap => snap.docs.map(d => d.data()))
      )).then(results => results.flat());
    }

    // ── Date range provided → chunk yearMonths into batches of 10 ──
    const yearMonths = this.getYearMonthsInRange(startDate, endDate);
    if (yearMonths.length === 0) return Promise.resolve([]);

    const ymChunks = this.chunkArray(yearMonths, this.FIRESTORE_IN_LIMIT);

    if (codes.length <= 1) {
      // Single centre or all centres — one query per yearMonth chunk
      return Promise.all(ymChunks.map(ymBatch => {
        let query: firebase.firestore.Query = this.mFireStore.firestore.collection(this.summaryCollection);
        query = query.where('yearMonth', 'in', ymBatch);
        if (codes.length === 1) query = query.where('centreCode', '==', codes[0]);
        return query.get().then(snap => snap.docs.map(d => d.data()));
      })).then(results => results.flat());
    }

    // Multiple centres + multiple yearMonth chunks — per-centre per-chunk queries
    const promises: Promise<any[]>[] = [];
    for (const ymBatch of ymChunks) {
      for (const code of codes) {
        let q: firebase.firestore.Query = this.mFireStore.firestore.collection(this.summaryCollection);
        q = q.where('yearMonth', 'in', ymBatch).where('centreCode', '==', code);
        promises.push(q.get().then(snap => snap.docs.map(d => d.data())));
      }
    }
    return Promise.all(promises).then(results => results.flat());
  }

  // ========================================================

  addIndividualSession(value: any) {
    //set up education trigger on complete
    const childName = value.childName;
    const centreName = value.centreName;
    const age = value.age;
    let taskDate = new Date()
    taskDate.setDate(taskDate.getDate() + 4)
    const {assignees} = this.taskService.getTaskForAndCollaboratorsEducationTrigger(value.centreCode, 'Rapport Session');
    const mEducationTask: Partial<Task> = {
      title: `Conduct Individual Session for ${childName} (${age} yrs)`,
      done: false,
      priority: 'medium',
      assignees: assignees,
      due_date: taskDate,
      note: `Conduct Individual session of for ${childName} of ${centreName}`,
    };
    this.taskService.addAutomatedTask(mEducationTask);

    // ✅ Maintain summary counter (individual only — followUp is tracked separately via EducationFollowUpAssessment)
    if (value.present === 'Present' && value.submittedBy && value.centreCode && value.sessionDate) {
      this.updateSummaryCounter(value.submittedBy, value.centreCode, value.sessionDate, {
        individual: 1
      }).catch(e => console.warn('⚠️ Summary counter update failed:', e));
    }

    return this.mFireStore.collection(this.env.education).add(value)
  }

  updateIndividualSession(value: any, id: string) {
    return this.mFireStore.collection(this.env.education).doc(id).update(value)
  }

  addGroupEducationSession(value: any) {
    // ✅ Maintain summary counter — group sessions store centre in centres[] OR root centreCode
    // If countType === 'bychildren', count = number of present children; otherwise count as 1 session
    if (value.submittedBy && value.sessionDate) {
      const presentChildren = Array.isArray(value.children)
        ? value.children.filter((c: any) => c.status === 'Present')
        : [];
      const groupCount = value.countType === 'bychildren' ? presentChildren.length : 1;

      if (groupCount > 0) {
        const centreCodes: string[] = [];
        if (value.centreCode) {
          centreCodes.push(value.centreCode);
        } else if (Array.isArray(value.centres) && value.centres.length > 0) {
          value.centres.forEach((c: any) => { if (c?.centreCode) centreCodes.push(c.centreCode); });
        }
        if (centreCodes.length === 0) centreCodes.push('All'); // fallback
        centreCodes.forEach(cc => {
          this.updateSummaryCounter(value.submittedBy, cc, value.sessionDate, { group: groupCount })
            .catch(e => console.warn('⚠️ Summary counter update failed:', e));
        });
      }
    }
    return this.mFireStore.collection(this.env.educationGroup).add(value)
  }

  editGroupEducationSession(value: any, id: string) {
    return this.mFireStore.collection(this.env.educationGroup).doc(id).update(value)
  }

  getEducation(centerCode: string) {
    let q = this.mFireStore.collection<any>(this.env.education, ref => ref.orderBy('sessionDate', 'desc').where('centreCode', '==', centerCode)).valueChanges({idField: 'id'})
    if (centerCode == 'All') q = this.mFireStore.collection<any>(this.env.education, ref => ref.orderBy('sessionDate', 'desc')).valueChanges({idField: 'id'})
    return q.pipe(take(1))
  }

  // getIndividualEducationList() {
  //   return this.mFireStore.collection<any>(this.env.education, ref => ref.orderBy('sessionDate', 'desc')).valueChanges({idField: 'id'})
  // }
  private individualEducation$!: Observable<any[]>;
  getIndividualEducationList(): Observable<any[]> {
    if (!this.individualEducation$) {

      this.individualEducation$ = this.mFireStore
        .collection<any>(
          this.env.education,
          ref => ref.orderBy('sessionDate', 'desc')
        )
        .valueChanges({ idField: 'id' })
        .pipe(
          shareReplay({ bufferSize: 1, refCount: true })
        );

    }

    return this.individualEducation$;
  }
  getIndividualEducationListPresentOnly2024(date: Date) {
    return this.mFireStore.collection<any>(this.env.education, ref => ref.orderBy('sessionDate', 'desc')
      .where('sessionDate', '>=', date)
      .where('present', '==', 'Present')
    ).valueChanges({idField: 'id'}).pipe(take(1))
  }

  //* Count-only variant using Firestore count() aggregation — avoids downloading full documents just to read .length
  getIndividualEducationListPresentOnly2024Count(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.education),
      where('sessionDate', '>=', date),
      where('present', '==', 'Present')
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  getIndividualEducationListPresentOnly2024Borivali(date: Date) {
    return this.mFireStore.collection<any>(this.env.education, ref => ref.orderBy('sessionDate', 'desc')
      .where('sessionDate', '>=', date)
      .where('present', '==', 'Present')
      .where('centreCode', '==', 'ALBorivali')
    ).valueChanges({idField: 'id'}).pipe(take(1))
  }

  //* Count-only variant using Firestore count() aggregation — avoids downloading full documents just to read .length
  getIndividualEducationListPresentOnly2024BorivaliCount(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.education),
      where('sessionDate', '>=', date),
      where('present', '==', 'Present'),
      where('centreCode', '==', 'ALBorivali')
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  //* Count-only variant using Firestore count() aggregation — avoids downloading full documents just to read .length
  // ── Centre + date-range counts, for the MIS dashboard cards ─────────────────
  // Each needs a composite index; Firestore's console error links create them.

  /** Present-only individual sessions for one centre over a date range. */
  countIndividualSessions(centreCode: string | null, startDate: Date | null, endDate: Date | null): Promise<number> {
    const filters: any[] = [where('present', '==', 'Present')];
    if (centreCode && centreCode !== 'All') filters.push(where('centreCode', '==', centreCode));
    if (startDate) filters.push(where('sessionDate', '>=', startDate));
    if (endDate) filters.push(where('sessionDate', '<=', endDate));
    const q = query(collection(getFirestore(), this.env.education), ...filters);
    return getCountFromServer(q).then(snap => snap.data().count);
  }

  /**
   * Group sessions for one centre over a date range.
   * Mirrors getGroupEducationSessionsByCenterCodeCount: the centre lives in a
   * `centres` array of {centreCode, centreName} objects, so this is object equality
   * inside an array and breaks if a centre is renamed. Note some group documents
   * carry a root-level centreCode instead — those will not match this filter.
   */
  countGroupSessions(centreCode: string | null, centreName: string, startDate: Date | null, endDate: Date | null): Promise<number> {
    const filters: any[] = [];
    if (centreCode && centreCode !== 'All') {
      filters.push(where('centres', 'array-contains', {centreCode, centreName}));
    }
    if (startDate) filters.push(where('sessionDate', '>=', startDate));
    if (endDate) filters.push(where('sessionDate', '<=', endDate));
    const q = query(collection(getFirestore(), this.env.educationGroup), ...filters);
    return getCountFromServer(q).then(snap => snap.data().count);
  }

  getIndividualEducationListByCenterCount(centreCode: string | undefined): Promise<number> {
    const q = centreCode != 'All'
      ? query(collection(getFirestore(), this.env.education), where('centreCode', '==', centreCode))
      : query(collection(getFirestore(), this.env.education))
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  getIndividualEducationList2024(date: Date) {
    return this.mFireStore.collection<any>(this.env.education, ref => ref.orderBy('sessionDate', 'desc')
      .where('sessionDate', '>=', date)).valueChanges({idField: 'id'}).pipe(take(1))
  }

  getIndividualEducationListByCenter(centreCode: string | undefined) {
    if (centreCode != 'All') {
      return this.mFireStore.collection<any>(this.env.education, ref => ref
        .where('centreCode', '==', centreCode)
        .orderBy('sessionDate', 'desc')).valueChanges({idField: 'id'}).pipe(take(1))
    } else {
      return this.mFireStore.collection<any>(this.env.education, ref => ref
        .orderBy('sessionDate', 'desc')).valueChanges({idField: 'id'}).pipe(take(1))
    }
  }

  getIndividualSessionByCentreOrDate(centreCode: string, start: Date, end: Date) {
    let ref = this.mFireStore.collection<any>(this.env.education, q => q
      .orderBy('sessionDate', 'desc')
      .where('sessionDate', '>=', start)
      .where('sessionDate', '<=', end)
    )
    if (centreCode != 'All') {
      ref = this.mFireStore.collection<any>(this.env.education, q => q
        .where('centreCode', '==', centreCode)
        .orderBy('sessionDate', 'desc')
        .where('sessionDate', '>=', start)
        .where('sessionDate', '<=', end)
      )
    }
    return ref.valueChanges({idField: 'id'}).pipe(take(1))
  }

  getIndividualEducationListByCenterByDate(centreCode
                                           :
                                             string | undefined, startDate
                                           :
                                           Date, endDate
                                           :
                                           Date
  ) {
    return this.mFireStore.collection<any>(this.env.education, ref => ref.orderBy('sessionDate', 'desc').where('centreCode', '==', centreCode)).valueChanges({idField: 'id'})
  }

// getGroupEducationSessions(centerCode: string, selectedCentre: any,) {
// let q= this.mFireStore.collection<any>(this.env.educationGroup, ref => ref
//         .orderBy("sessionDate", "desc")
//         .where('centres', 'array-contains', selectedCentre))
//         .valueChanges({idField: 'id'})
//
//  if(centerCode =='All') {
//      q = this.mFireStore.collection<any>(this.env.educationGroup, ref => ref
//          .orderBy("sessionDate", "desc"))
//          // .where('centres', 'array-contains', selectedCentre))
//          .valueChanges({idField: 'id'})
//
//  }
//     return q
// }
  getGroupEducationSessions(centerCode
                            :
                            string, selectedCentre
                            :
                            any, volunteer ?: string
  ) {
    let queryFn: any;

    if (centerCode === 'All') {
      // No center filter
      queryFn = (ref: any) => {
        let q = ref.orderBy('sessionDate', 'desc');
        if (volunteer && volunteer !== 'All') {
          q = q.where('volunteerName', '==', volunteer);
        }
        return q;
      };
    } else {
      // Filter by selectedCentre
      queryFn = (ref: any) => {
        let q = ref.orderBy('sessionDate', 'desc').where('centres', 'array-contains', selectedCentre);
        if (volunteer && volunteer !== 'All') {
          q = q.where('volunteerName', '==', volunteer);
        }
        return q;
      };
    }

    return this.mFireStore.collection<any>(this.env.educationGroup, queryFn).valueChanges({idField: 'id'});
  }

  getGroupEducationSessionsCount(centreCode: string | string[], startDate?: Date | null | undefined, endDate?: Date | null | undefined,){
    const codes: string[] = Array.isArray(centreCode)
      ? centreCode.filter(c => !!c && c !== 'All')
      : (centreCode && centreCode !== 'All' ? [centreCode] : []);
    console.log('centercode(s)', codes)

    const buildQuery = (filterCodes: string[]): firebase.firestore.Query<any> => {
      let myQuery: firebase.firestore.Query<any> = this.mFireStore.firestore.collection(this.env.educationGroup).orderBy('sessionDate', 'desc');
      if (filterCodes.length === 1) myQuery = myQuery.where('centreCode', '==', filterCodes[0]);
      else if (filterCodes.length > 1) myQuery = myQuery.where('centreCode', 'in', filterCodes);
      if (startDate && endDate) myQuery = myQuery.where('sessionDate', '>=', startDate).where('sessionDate', '<=', endDate);
      return myQuery;
    };

    if (codes.length <= this.FIRESTORE_IN_LIMIT) {
      return buildQuery(codes).get();
    }

    // Chunk codes into batches of 10 and run parallel queries
    const chunks = this.chunkArray(codes, this.FIRESTORE_IN_LIMIT);
    return Promise.all(chunks.map(chunk => buildQuery(chunk).get())).then(snapshots => {
      const allDocs = snapshots.flatMap(s => s.docs);
      return { docs: allDocs } as any;
    });
  }
  /**
   * ✅ FIXED: Queries EducationFollowUpAssessment (not Counselling).
   * Date field is 'date' (Timestamp), not 'dateOfAssessment'.
   * Supports single centreCode or array of centreCodes.
   */
  getFollowUpAssCount(
    centreCode: string | string[],
    startDate?: Date | null | undefined,
    endDate?: Date | null | undefined
  ) {
    const codes: string[] = Array.isArray(centreCode)
      ? centreCode.filter(c => !!c && c !== 'All')
      : (centreCode && centreCode !== 'All' ? [centreCode] : []);

    const buildQueryFn = (filterCodes: string[]) => {
      return (ref: any) => {
        let query: firebase.firestore.Query<any> = ref.orderBy('date', 'desc');
        if (filterCodes.length === 1) query = query.where('centreCode', '==', filterCodes[0]);
        else if (filterCodes.length > 1) query = query.where('centreCode', 'in', filterCodes);
        if (startDate && endDate) {
          query = query.where('date', '>=', startDate).where('date', '<=', endDate);
        }
        return query;
      };
    };

    if (codes.length <= this.FIRESTORE_IN_LIMIT) {
      return this.mFireStore.collection(this.env.educationFollowUp, buildQueryFn(codes))
        .valueChanges({ idField: 'id' });
    }

    // Chunk codes into batches of 10 and merge observable results
    const chunks = this.chunkArray(codes, this.FIRESTORE_IN_LIMIT);
    const observables = chunks.map(chunk =>
      this.mFireStore.collection(this.env.educationFollowUp, buildQueryFn(chunk))
        .valueChanges({ idField: 'id' })
    );
    return combineLatest(observables).pipe(
      map(results => results.flat())
    );
  }

  /**
   * Add a follow-up assessment and increment summary counter.
   * Use this instead of calling Firestore directly.
   */
  addFollowUpAssessment(value: any): Promise<any> {
    if (value.submittedBy && value.centreCode && value.date) {
      this.updateSummaryCounter(value.submittedBy, value.centreCode, value.date, { followUp: 1 })
        .catch(e => console.warn('⚠️ FollowUp summary counter update failed:', e));
    }
    return this.mFireStore.collection(this.env.educationFollowUp).add(value);
  }

  /**
   * Delete a follow-up assessment and decrement summary counter.
   * Use this instead of calling Firestore directly.
   */
  getFollowUpHighlights(
    centreCode?: string,
    startDate?: Date,
    endDate?: Date,
    sessionType?: string | null
  ) {
    let query: firebase.firestore.Query<any> = this.mFireStore.firestore
      .collection(this.env.educationFollowUp)
      .orderBy('date', 'desc')
      .where('isHighlight', '==', true);

    if (centreCode && centreCode !== 'All')
      query = query.where('centreCode', '==', centreCode);

    if (startDate && endDate)
      query = query.where('date', '>=', startDate).where('date', '<=', endDate);

    if (sessionType && sessionType !== 'All')
      query = query.where('sessionType', '==', sessionType);

    return query.get();
  }

  toggleFollowUpHighlight(element: any): Promise<void> {
    const newHighlightValue = !element.isHighlight;
    return this.mFireStore.collection(this.env.educationFollowUp).doc(element.id).update({ isHighlight: newHighlightValue });
  }

  deleteFollowUpAssessment(element: any): Promise<void> {
    if (element.submittedBy && element.centreCode && element.date) {
      this.updateSummaryCounter(element.submittedBy, element.centreCode, element.date, { followUp: -1 })
        .catch(e => console.warn('⚠️ FollowUp summary counter update failed:', e));
    }
    return this.mFireStore.collection(this.env.educationFollowUp).doc(element.id).delete();
  }


  // getGroupEducationSessions2() {
  //   return this.mFireStore.collection<any>(this.env.educationGroup).valueChanges({idField: 'id'})
  // }
  private groupSessions$!: Observable<any[]>;

  getGroupEducationSessions2(): Observable<any[]> {

    if (!this.groupSessions$) {
      this.groupSessions$ = this.mFireStore
        .collection<any>(this.env.educationGroup)
        .valueChanges({ idField: 'id' })
        .pipe(
          shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    return this.groupSessions$;
  }

  getGroupEducationSessions2024(date
                                :
                                Date
  ) {
    return this.mFireStore.collection<any>(this.env.educationGroup, ref => ref.where('sessionDate', '>=', date)).valueChanges({idField: 'id'})
  }

  //* Count-only variant using Firestore count() aggregation — avoids downloading full documents just to read .length
  getGroupEducationSessions2024Count(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.educationGroup),
      where('sessionDate', '>=', date)
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  getGroupEducationSessions2024Borivali(date
                                        :
                                        Date
  ) {
    return this.mFireStore.collection<any>(this.env.educationGroup, ref => ref.where('sessionDate', '>=', date)
      .where('centreCode', '==', 'ALBorivali')
    ).valueChanges({idField: 'id'})
  }

  //* Count-only variant using Firestore count() aggregation — avoids downloading full documents just to read .length
  getGroupEducationSessions2024BorivaliCount(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.educationGroup),
      where('sessionDate', '>=', date),
      where('centreCode', '==', 'ALBorivali')
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  getGroupEducationSessionsByCenter(childObject
                                    :
                                    any,
  ) {
    return this.mFireStore.collection<any>(this.env.educationGroup, ref => ref.orderBy('sessionDate', 'desc').where('children', 'array-contains', childObject)).valueChanges({idField: 'id'})
  }

  getGroupEducationSessionsByCenterCode(centreObject
                                        :
                                        any,
  ) {
    return this.mFireStore.collection<any>(this.env.educationGroup, ref => ref.orderBy('sessionDate', 'desc').where('centres', 'array-contains', centreObject)).valueChanges({idField: 'id'})
  }

  //* Count-only variant using Firestore count() aggregation — avoids downloading full documents just to read .length
  getGroupEducationSessionsByCenterCodeCount(centreObject: any): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.educationGroup),
      where('centres', 'array-contains', centreObject)
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  deleteIndividualSession(element: any) {
    // ✅ Decrement summary counter
    if (element.present === 'Present' && element.submittedBy && element.centreCode && element.sessionDate) {
      const isFollowUp = element.sessionType === 'Follow Up' || element.sessionType === 'Discharge Assessment';
      this.updateSummaryCounter(element.submittedBy, element.centreCode, element.sessionDate, {
        individual: -1
      }).catch(e => console.warn('⚠️ Summary counter update failed:', e));
    }
    return this.mFireStore.collection(this.env.education).doc(element.id).delete();
  }

  deleteGroupSession(element: any) {
    // ✅ Decrement summary counter — match same centreCode + countType logic as add
    if (element.submittedBy && element.sessionDate) {
      const presentChildren = Array.isArray(element.children)
        ? element.children.filter((c: any) => c.status === 'Present')
        : [];
      const groupCount = element.countType === 'bychildren' ? presentChildren.length : 1;

      if (groupCount > 0) {
        const centreCodes: string[] = [];
        if (element.centreCode) {
          centreCodes.push(element.centreCode);
        } else if (Array.isArray(element.centres) && element.centres.length > 0) {
          element.centres.forEach((c: any) => { if (c?.centreCode) centreCodes.push(c.centreCode); });
        }
        if (centreCodes.length === 0) centreCodes.push('All');
        centreCodes.forEach(cc => {
          this.updateSummaryCounter(element.submittedBy, cc, element.sessionDate, { group: -groupCount })
            .catch(e => console.warn('⚠️ Summary counter update failed:', e));
        });
      }
    }
    return this.mFireStore.collection(this.env.educationGroup).doc(element.id).delete();
  }

  getIndividualSessionsOfAChild(selectedChild
                                :
                                FullChild
  ) {
    return this.mFireStore.collection<any>(this.env.education, ref => ref.where('childCode', '==', selectedChild.childCode)).valueChanges({idField: 'id'})

  }

  getLastDischargeAssessmentByChild(childCode
                                    :
                                    string
  ) {
    return this.mFireStore.collection(environment.productionMode.education, ref => ref.where('childCode', '==', childCode).where('sessionType', '==', 'Discharge Assessment')).valueChanges()
  }

  getTotalSessions(childCode
                   :
                   string, subject
                   :
                   string
  ) {
    return this.mFireStore.collection(environment.productionMode.education, ref => ref.where('childCode', '==', childCode).where('present', '==', 'Present').where('subject', 'array-contains', subject)).valueChanges()
  }

  getChildIndividualDischargeSession(childCode
                                     :
                                     string
  ) {
    return this.mFireStore.collection(environment.productionMode.education, ref => ref.where('sessionType', '==', 'Discharge Assessment').where('childCode', '==', childCode)).valueChanges()
  }

  dischargeAssssmentSessionChildren(centreCode: string) {
    return this.mFireStore.collection(this.env.education, ref => ref.where('centreCode', '==', centreCode).where('sessionType', '==', 'Discharge Assessment')).valueChanges()

  }

  fetchSpecialDaysWithinDateRange(startDate: Date, endDate: Date, selectedCentre: string) {
    let mStartDate  =startDate.setHours(0,0,0,0)
   let  mEndDate = endDate.setHours(23,59,59,999)
    const query$ = (this.mFireStore.collection(this.env.educationGroup, ref => ref.where('sessionDate', '>=', mStartDate).where('sessionDate', '<=', mEndDate).where('centreCode', '==', selectedCentre)
      .where('sessionType','==','Special Day')
    ).get())

    return  firstValueFrom(query$)
  }
  toggleIndividualSessionHighlight(element: any) {
    const dockey = element.id;
    const newHighlightValue = !element.isHighlight; // Toggle the current value
    const allSessionRef = this.mFireStore.doc(`${this.env.education}/${dockey}`);

    return allSessionRef.update({ isHighlight: newHighlightValue });
  }

  getGroupSessionsOfAChild(childCode: FullChild) {
    return this.mFireStore.collection<any>(this.env.educationGroup, ref => ref.where('children', '==', childCode)).valueChanges({idField: 'id'})
  }

  /**
   * Fetch all group sessions for a specific child by childCode.
   * Filters client-side because Firestore cannot query nested object fields inside arrays.
   * Optionally filter by centreCode and date range.
   *
   * Group session structure:
   *   children: [{ childCode, childName, gender, isFollowUp, status, unitCode }]
   *   centres:  [{ centreCode, centreName }]
   *   subject:  string | string[]   (e.g. 'Maths', 'EVS/GK', 'Language')
   *   sessionDate: Timestamp
   *   centreCode: string (root level)
   */


  /**
   * Fetch ALL group sessions for a specific child by childCode (lifetime — no centre/date filter).
   * Firestore cannot query nested array fields, so we fetch all sessions ordered by date
   * and filter client-side by childCode.
   */
  getGroupSessionsByChildCode(childCode: string): Promise<any[]> {
    return this.mFireStore.firestore
      .collection(this.env.educationGroup)
      .orderBy('sessionDate', 'desc')
      .get()
      .then(snapshot =>
        snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() as any }))
          .filter(session => {
            const children: any[] = session['children'] || [];
            return children.some((child: any) => child.childCode === childCode);
          })
      );
  }

  /**
   * Get group session subject counts for a specific child (complete lifetime totals).
   *
   * Subject keyword mapping:
   *   Maths    → 'math'
   *   EVS/GK   → 'evs', 'gk', 'g.k', 'environment', 'science'
   *   Language → 'language', 'english', 'hindi', 'marathi', 'tamil', 'kannada', 'gujarati'
   *
   * Only sessions where the child's status === 'Present' are counted.
   * No date/centre filter — returns total session counts across the child's entire history.
   */
  getGroupSessionSubjectCountsForChild(childCode: string): Promise<{
    maths: number;
    evsGk: number;
    language: number;
    other: number;
    total: number;
    sessions: any[];
  }> {
    return this.getGroupSessionsByChildCode(childCode).then(sessions => {
      // Only count sessions where the child was Present
      const presentSessions = sessions.filter(session => {
        const children: any[] = session['children'] || [];
        return children.some((child: any) =>
          child.childCode === childCode && child.status === 'Present'
        );
      });

      const counts = { maths: 0, evsGk: 0, language: 0, other: 0, total: presentSessions.length };

      presentSessions.forEach(session => {
        const rawSubject = session['subject'];
        const subjects: string[] = Array.isArray(rawSubject)
          ? rawSubject
          : rawSubject ? [rawSubject] : [];

        if (subjects.length === 0) {
          counts.other++;
          return;
        }

        // A session can have multiple subjects (e.g. ['Math', 'Language']).
        // Each subject is counted independently in its category.
        subjects.forEach((sub: string) => {
          const s = (sub || '').toLowerCase().trim();
          if (s === 'math' || s === 'maths') {
            counts.maths++;
          } else if (s === 'evs' || s === 'evs/gk' || s === 'evsgk' || s === 'evs gk') {
            counts.evsGk++;
          } else if (s === 'language') {
            counts.language++;
          } else {
            counts.other++;
          }
        });
      });

      return { ...counts, sessions: presentSessions };
    });
  }
}
